import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HintTooltip from '../common/HintTooltip';
import { instanciaService, MetaBusinessProfile } from '../../services/instanciaService';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';

interface MetaBusinessProfileDialogProps {
  instanciaId: string;
  open: boolean;
  onClose: () => void;
}

// Segmentos aceitos pela Meta; o rótulo vem do i18n (businessProfile.verticals).
const VERTICAL_OPTIONS = [
  'UNDEFINED', 'OTHER', 'AUTO', 'BEAUTY', 'APPAREL', 'EDU', 'ENTERTAIN', 'EVENT_PLAN', 'FINANCE',
  'GROCERY', 'GOVT', 'HOTEL', 'HEALTH', 'NONPROFIT', 'PROF_SERVICES', 'RETAIL', 'TRAVEL', 'RESTAURANT',
];

const MetaBusinessProfileDialog: React.FC<MetaBusinessProfileDialogProps> = ({ instanciaId, open, onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<MetaBusinessProfile>({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    instanciaService.obterPerfilComercial(instanciaId)
      .then((res) => setProfile(res.data || {}))
      .catch((err: any) => {
        console.error('Erro ao obter perfil comercial:', err);
        toast({
          title: t('businessProfile.loadErrorTitle'),
          description: err.response?.data?.message || t('businessProfile.loadErrorDesc'),
          variant: 'destructive',
        });
      })
      .finally(() => setLoading(false));
  }, [open, instanciaId, toast, t]);

  const setField = (field: keyof MetaBusinessProfile) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const website = (profile.websites && profile.websites[0]) || '';
      await instanciaService.atualizarPerfilComercial(instanciaId, {
        about: profile.about || '',
        description: profile.description || '',
        address: profile.address || '',
        email: profile.email || '',
        vertical: profile.vertical || 'UNDEFINED',
        websites: website ? [website] : [],
      });
      toast({ title: t('businessProfile.savedTitle'), description: t('businessProfile.savedDesc') });
      onClose();
    } catch (err: any) {
      console.error('Erro ao atualizar perfil comercial:', err);
      toast({
        title: t('businessProfile.saveErrorTitle'),
        description: err.response?.data?.message || t('businessProfile.saveErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg dark:bg-gray-800">
        <DialogHeader>
          <DialogTitle className="dark:text-white">{t('businessProfile.title')}</DialogTitle>
          <DialogDescription className="dark:text-gray-400">
            {t('businessProfile.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t('businessProfile.loading')}
          </div>
        ) : (
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="about" className="dark:text-gray-300">{t('businessProfile.about')}</Label>
              <Input
                id="about"
                value={profile.about || ''}
                onChange={setField('about')}
                maxLength={139}
                placeholder={t('businessProfile.aboutPlaceholder')}
                className="dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="description" className="dark:text-gray-300">{t('businessProfile.descriptionLabel')}</Label>
              <Textarea
                id="description"
                value={profile.description || ''}
                onChange={setField('description')}
                maxLength={512}
                rows={3}
                placeholder={t('businessProfile.descriptionPlaceholder')}
                className="dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="address" className="dark:text-gray-300">{t('businessProfile.address')}</Label>
              <Input
                id="address"
                value={profile.address || ''}
                onChange={setField('address')}
                maxLength={256}
                className="dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="email" className="dark:text-gray-300">{t('businessProfile.email')}</Label>
              <Input
                id="email"
                type="email"
                value={profile.email || ''}
                onChange={setField('email')}
                maxLength={128}
                className="dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="website" className="dark:text-gray-300">{t('businessProfile.website')}</Label>
              <Input
                id="website"
                value={(profile.websites && profile.websites[0]) || ''}
                onChange={(e) => setProfile((prev) => ({ ...prev, websites: [e.target.value] }))}
                placeholder="https://"
                className="dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="vertical" className="dark:text-gray-300">{t('businessProfile.vertical')}</Label>
              <select
                id="vertical"
                value={profile.vertical || 'UNDEFINED'}
                onChange={setField('vertical') as any}
                className="flex h-9 w-full rounded-md border border-zinc-200 bg-transparent px-3 py-2 text-sm shadow-sm dark:border-zinc-800 dark:bg-gray-700 dark:text-white"
              >
                {VERTICAL_OPTIONS.map((value) => (
                  <option key={value} value={value}>{t(`businessProfile.verticals.${value}`)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <HintTooltip text={t('businessProfile.saveHint')}>
            <Button onClick={handleSave} disabled={loading || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('common.save')}
            </Button>
          </HintTooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MetaBusinessProfileDialog;
